import { SignaturesManager } from "@/components/staff/SignaturesManager";
import { DataResetPanel } from "@/components/staff/DataResetPanel";
import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Settings, Loader2, Save, Clock, User, Shield, FileText, Video, Wifi, WifiOff, ExternalLink, Link2, Award, ToggleLeft, ToggleRight, AlertTriangle } from "lucide-react";

const SettingsPage = () => {
  const { token, user } = useAuth();
  const [tab, setTab] = useState("programme");
  const [settings, setSettings] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [zohoUrl, setZohoUrl] = useState("");
  const [savingZoho, setSavingZoho] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchAuditLog();
    fetchCalendarStatus();
    fetchZohoConfig();

    // Check for calendar connection result from OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar") === "connected") {
      toast.success(`Google Calendar connected (${params.get("email") || ""})`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("calendar") === "error") {
      toast.error(`Calendar connection failed: ${params.get("reason") || "unknown error"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("zoho") === "connected") {
      toast.success("Zoho Bookings connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("zoho") === "error") {
      toast.error(`Zoho connection failed: ${params.get("reason") || "unknown error"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/staff/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await fetch(`${API}/staff/audit-log?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAuditLog(d.events); setAuditTotal(d.total); }
    } catch (e) { /* may fail for non-admin */ }
  };

  const fetchCalendarStatus = async () => {
    try {
      const res = await fetch(`${API}/staff/calendar/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCalendarStatus(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchZohoConfig = async () => {
    try {
      const res = await fetch(`${API}/booking/zoho-config`);
      if (res.ok) { const d = await res.json(); setZohoUrl(d.zoho_bookings_url || ""); }
    } catch (e) { console.error(e); }
  };

  const handleConnectCalendar = async () => {
    try {
      const res = await fetch(`${API}/oauth/calendar/connect`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authorization_url;
      } else toast.error("Failed to start calendar connection");
    } catch { toast.error("Connection error"); }
  };

  const handleDisconnectCalendar = async () => {
    try {
      const res = await fetch(`${API}/staff/calendar/disconnect`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Google Calendar disconnected"); fetchCalendarStatus(); }
    } catch { toast.error("Failed"); }
  };

  const handleSaveZoho = async () => {
    setSavingZoho(true);
    try {
      const res = await fetch(`${API}/staff/zoho-bookings-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ zoho_bookings_url: zohoUrl }),
      });
      if (res.ok) toast.success("Zoho Bookings URL saved");
      else toast.error("Failed to save");
    } catch { toast.error("Connection error"); }
    finally { setSavingZoho(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          programme_name: settings.programme_name,
          programme_price: settings.programme_price,
          programme_currency: settings.programme_currency,
          programme_duration_weeks: settings.programme_duration_weeks,
          renewal_duration_weeks: settings.renewal_duration_weeks,
          renewal_price: settings.renewal_price,
          whatsapp_number: settings.whatsapp_number,
          booking_url: settings.booking_url,
        }),
      });
      if (res.ok) { toast.success("Settings saved."); fetchAuditLog(); }
      else toast.error("Failed to save.");
    } catch { toast.error("Connection error."); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Settings and Audit Log</h1>
        <p className="mt-1 text-base text-stone-500">System configuration, programme settings and activity history</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg flex-wrap">
          <TabsTrigger value="programme" className="rounded-md text-sm">Programme</TabsTrigger>
          <TabsTrigger value="credentials" className="rounded-md text-sm" data-testid="tab-credentials">Signatures</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-md text-sm" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="scheduler" className="rounded-md text-sm" data-testid="tab-scheduler">Background Jobs</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="data-reset" className="rounded-md text-sm" data-testid="tab-data-reset">Data Reset</TabsTrigger>}
          <TabsTrigger value="audit" className="rounded-md text-sm">Audit Log ({auditTotal})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "programme" && settings && (
        <Card className="rounded-xl border-stone-200/60 p-6 space-y-5">
          <h3 className="text-base font-bold text-stone-900">Programme configuration</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Programme name</label>
              <Input value={settings.programme_name || ""} onChange={e => update("programme_name", e.target.value)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Programme price</label>
              <div className="flex gap-2">
                <Input type="number" value={settings.programme_price || ""} onChange={e => update("programme_price", parseInt(e.target.value) || 0)} className="rounded-lg flex-1" />
                <Input value={settings.programme_currency || "NGN"} onChange={e => update("programme_currency", e.target.value)} className="rounded-lg w-20" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Programme duration (weeks)</label>
              <Input type="number" value={settings.programme_duration_weeks || ""} onChange={e => update("programme_duration_weeks", parseInt(e.target.value) || 12)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Renewal duration (weeks)</label>
              <Input type="number" value={settings.renewal_duration_weeks || ""} onChange={e => update("renewal_duration_weeks", parseInt(e.target.value) || 6)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Renewal price</label>
              <Input type="number" value={settings.renewal_price || ""} onChange={e => update("renewal_price", parseInt(e.target.value) || 0)} className="rounded-lg" placeholder="Configurable" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">WhatsApp number</label>
              <Input value={settings.whatsapp_number || ""} onChange={e => update("whatsapp_number", e.target.value)} className="rounded-lg" placeholder="+234..." />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1.5 block">Booking URL (Zoho Bookings or custom page)</label>
            <Input value={settings.booking_url || ""} onChange={e => update("booking_url", e.target.value)} className="rounded-lg" placeholder="https://..." />
          </div>

          <h3 className="text-base font-bold text-stone-900 pt-4 border-t border-stone-100">Intake form reminders</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Max reminders for incomplete forms</label>
              <Input type="number" value={settings.max_intake_reminders ?? 3} onChange={e => update("max_intake_reminders", parseInt(e.target.value) || 3)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Reminder interval (hours)</label>
              <Input type="number" value={settings.intake_reminder_interval_hours ?? 24} onChange={e => update("intake_reminder_interval_hours", parseInt(e.target.value) || 24)} className="rounded-lg" />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Settings
          </Button>
        </Card>
      )}

      {tab === "integrations" && (
        <div className="space-y-4">
          {/* Google Calendar + Meet */}
          <Card className="rounded-xl border-stone-200/60 p-6 space-y-4" data-testid="calendar-integration">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${calendarStatus?.connected ? "bg-green-100" : "bg-stone-100"}`}>
                  <Video className={`w-5 h-5 ${calendarStatus?.connected ? "text-green-600" : "text-stone-400"}`} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-stone-900">Google Calendar + Meet</h3>
                  <p className="text-xs text-stone-500">Auto-create Google Meet links when bookings are confirmed</p>
                </div>
              </div>
              {calendarStatus?.connected ? (
                <Badge className="bg-green-50 text-green-700 border-green-200 text-xs"><Wifi className="w-3 h-3 mr-1" /> Connected</Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-stone-400"><WifiOff className="w-3 h-3 mr-1" /> Not connected</Badge>
              )}
            </div>

            {calendarStatus?.connected ? (
              <div className="bg-green-50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-green-800">Connected as <strong>{calendarStatus.google_email}</strong></p>
                <p className="text-xs text-green-600">Connected on {calendarStatus.connected_at?.split("T")[0]}</p>
                <Button variant="outline" size="sm" className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleDisconnectCalendar} data-testid="disconnect-calendar-btn">
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="bg-stone-50 rounded-lg p-4 space-y-3">
                <p className="text-sm text-stone-600">Connect your Google account to automatically create Google Meet video links for patient consultations.</p>
                <Button onClick={handleConnectCalendar} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm" data-testid="connect-calendar-btn">
                  <Video className="w-4 h-4 mr-1.5" /> Connect Google Calendar
                </Button>
              </div>
            )}

            {calendarStatus?.system_calendar_available && !calendarStatus?.connected && (
              <p className="text-xs text-stone-500">System calendar available via {calendarStatus.system_calendar_email}. Bookings will use this account for Meet links.</p>
            )}
          </Card>

          {/* Zoho Bookings API Integration */}
          <ZohoBookingsCard token={token} zohoUrl={zohoUrl} setZohoUrl={setZohoUrl} handleSaveZoho={handleSaveZoho} savingZoho={savingZoho} />
        </div>
      )}

      {tab === "audit" && (
        <Card className="rounded-xl border-stone-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100">
            <h3 className="text-base font-bold text-stone-900">Activity log</h3>
          </div>
          {auditLog.length > 0 ? (
            <div className="divide-y divide-stone-100">
              {auditLog.map(ev => (
                <div key={ev.event_id} className="px-5 py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Shield className="w-4 h-4 text-stone-500" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-900 break-words">{typeof ev.details === "string" ? ev.details : JSON.stringify(ev.details)}</p>
                    <p className="text-xs text-stone-400">{ev.user_name || ev.user_id} &middot; {ev.timestamp?.split("T")[0]} {ev.timestamp?.split("T")[1]?.split(".")[0]}</p>
                  </div>
                  <Badge variant="outline" className="text-xs capitalize ml-auto shrink-0">{ev.event_type?.replace(/_/g, " ")}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-stone-400">
              <Clock className="w-8 h-8 text-stone-300 mx-auto mb-2" />
              No audit events recorded yet. Events are logged when settings are changed, reports are approved, and other important actions occur.
            </div>
          )}
        </Card>
      )}

      {tab === "credentials" && <SignaturesManager token={token} />}
      {tab === "data-reset" && user?.role === "admin" && <DataResetPanel token={token} />}

      {tab === "scheduler" && <SchedulerTab token={token} />}
    </div>
  );
};

// ========== CREDENTIALS TAB ==========
// ========== SCHEDULER TAB ==========
const SchedulerTab = ({ token }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API}/admin/scheduler/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setJobs(d.jobs || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleJob = async (jobId, enabled) => {
    setToggling(jobId);
    try {
      const res = await fetch(`${API}/admin/scheduler/jobs/toggle`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ job_id: jobId, enabled }),
      });
      if (res.ok) {
        toast.success(`Job ${enabled ? "enabled" : "disabled"}.`);
        setJobs(prev => prev.map(j => j.job_id === jobId ? { ...j, enabled } : j));
      } else toast.error("Failed to toggle job.");
    } catch { toast.error("Connection error."); }
    finally { setToggling(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

  return (
    <Card className="rounded-xl border-stone-200/60 overflow-hidden" data-testid="scheduler-tab">
      <div className="px-5 py-4 border-b border-stone-100">
        <h3 className="text-base font-bold text-stone-900">Background Jobs</h3>
        <p className="text-xs text-stone-500 mt-1">Toggle scheduled email jobs on or off. Legacy patient-facing jobs are disabled by default.</p>
      </div>
      <div className="divide-y divide-stone-100">
        {jobs.map(job => (
          <div key={job.job_id} className="px-5 py-3.5 flex items-center justify-between gap-4" data-testid={`job-${job.job_id}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {job.is_legacy && (
                <span title="Legacy job from previous app structure">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                </span>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{job.description}</p>
                {job.is_legacy && <p className="text-xs text-amber-600">Legacy: from previous patient-facing app</p>}
              </div>
            </div>
            <button
              onClick={() => toggleJob(job.job_id, !job.enabled)}
              disabled={toggling === job.job_id}
              className="shrink-0"
              data-testid={`toggle-${job.job_id}`}
            >
              {toggling === job.job_id ? (
                <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
              ) : job.enabled ? (
                <ToggleRight className="w-7 h-7 text-green-600" />
              ) : (
                <ToggleLeft className="w-7 h-7 text-stone-300" />
              )}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ========== ZOHO BOOKINGS CARD ==========
const ZohoBookingsCard = ({ token, zohoUrl, setZohoUrl, handleSaveZoho, savingZoho }) => {
  const [zohoStatus, setZohoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/staff/zoho/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setZohoStatus(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleConnect = async () => {
    try {
      const res = await fetch(`${API}/zoho/oauth/start`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authorization_url;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to start Zoho connection");
      }
    } catch { toast.error("Connection error"); }
  };

  const handleDisconnect = async () => {
    try {
      const res = await fetch(`${API}/staff/zoho/disconnect`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Zoho Bookings disconnected"); setZohoStatus(prev => ({ ...prev, connected: false })); setTestResult(null); }
    } catch { toast.error("Failed to disconnect"); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/staff/zoho/test`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setTestResult(data);
        if (data.status === "ok") toast.success(data.message);
        else toast.error(data.message);
      }
    } catch { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  if (loading) return <Card className="rounded-xl border-stone-200/60 p-6"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></Card>;

  return (
    <Card className="rounded-xl border-stone-200/60 p-6 space-y-4" data-testid="zoho-integration">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${zohoStatus?.connected ? "bg-green-100" : "bg-blue-100"}`}>
            <Link2 className={`w-5 h-5 ${zohoStatus?.connected ? "text-green-600" : "text-blue-600"}`} />
          </div>
          <div>
            <h3 className="text-base font-bold text-stone-900">Zoho Bookings</h3>
            <p className="text-xs text-stone-500">Two-way calendar sync for consultations and follow-ups</p>
          </div>
        </div>
        {zohoStatus?.connected ? (
          <Badge className="bg-green-50 text-green-700 border-green-200 text-xs"><Wifi className="w-3 h-3 mr-1" /> Connected</Badge>
        ) : zohoStatus?.credentials_configured ? (
          <Badge variant="outline" className="text-xs text-amber-600 border-amber-200"><WifiOff className="w-3 h-3 mr-1" /> Not connected</Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-stone-400"><WifiOff className="w-3 h-3 mr-1" /> Not configured</Badge>
        )}
      </div>

      {zohoStatus?.connected ? (
        <div className="space-y-3">
          <div className="bg-green-50 rounded-lg p-4 space-y-2">
            <p className="text-sm text-green-800">Connected to workspace: <strong>{zohoStatus.workspace || "healthycooking"}</strong></p>
            <p className="text-xs text-green-600">Connected on {zohoStatus.connected_at?.split("T")[0]}</p>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleTest} disabled={testing} data-testid="test-zoho-btn">
                {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wifi className="w-3 h-3 mr-1" />} Test Connection
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleDisconnect} data-testid="disconnect-zoho-btn">
                Disconnect
              </Button>
            </div>
          </div>
          {testResult && (
            <div className={`rounded-lg p-3 text-xs ${testResult.status === "ok" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              <p className="font-medium">{testResult.message}</p>
              {testResult.workspaces?.length > 0 && (
                <ul className="mt-1 pl-4 list-disc">
                  {testResult.workspaces.map((w, i) => <li key={i}>{w.name} (ID: {w.id})</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-stone-50 rounded-lg p-4 space-y-3">
          <p className="text-sm text-stone-600">Connect Zoho Bookings to sync your shared calendar. New bookings in PlateMind will check availability and create appointments in Zoho. Bookings made directly in Zoho will appear in PlateMind.</p>
          {zohoStatus?.credentials_configured ? (
            <Button onClick={handleConnect} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm" data-testid="connect-zoho-btn">
              <Link2 className="w-4 h-4 mr-1.5" /> Connect Zoho Bookings
            </Button>
          ) : (
            <p className="text-xs text-red-600">Zoho API credentials not configured. Contact support to set up the integration.</p>
          )}
        </div>
      )}

      {/* Fallback: Zoho embed URL (preserved for existing booking page) */}
      <div className="pt-3 border-t border-stone-100 space-y-2">
        <p className="text-xs font-medium text-stone-500">Fallback: Zoho Bookings embed URL</p>
        <div className="flex gap-2">
          <Input value={zohoUrl} onChange={e => setZohoUrl(e.target.value)} placeholder="https://healthycooking.zohobookings.com/portal-embed#/..." className="rounded-lg flex-1 text-sm" data-testid="zoho-url-input" />
          <Button onClick={handleSaveZoho} disabled={savingZoho} variant="outline" size="sm" className="rounded-lg" data-testid="save-zoho-btn">
            {savingZoho ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-stone-400">This embed URL is used on the /consultation page as a fallback booking experience.</p>
      </div>
    </Card>
  );
};

export default SettingsPage;
